import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiCheck, FiArrowLeft } = FiIcons;

const PricingCard = ({ plan, index }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    socialHandle: ''
  });

  const handleFlip = () => { setIsFlipped(!isFlipped); };
  const handleBackButtonClick = (e) => { e.stopPropagation(); setIsFlipped(false); };
  const handleInputInteraction = (e) => { e.stopPropagation(); };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setStatus('submitting');

    try {
      const { error } = await supabase.from('leads').insert({
        full_name: formData.fullName,
        email: formData.email,
        social_handle: formData.socialHandle,
        plan_selected: plan.name,
        source: 'pricing',
        intent_level: 'High'
      });

      if (error) throw error;
      setStatus('success');
      setFormData({ fullName: '', email: '', socialHandle: '' }); // Reset form
    } catch (error) {
      console.error('Error submitting lead:', error);
      setStatus('error');
    }
  };

  const priceAnchors = { "Essential": "397", "Executive": "597", "Studio": "997" };
  const wasPrice = priceAnchors[plan.name];

  return (
    <div className="relative h-[345px] md:h-[600px] w-full perspective-1000 cursor-pointer group" onClick={handleFlip} >
      <motion.div className="w-full h-full relative preserve-3d" initial={false} animate={{ rotateY: isFlipped ? 180 : 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} >
        {/* Front Face */}
        <div className={`absolute inset-0 backface-hidden p-5 md:p-10 rounded-[1.75rem] md:rounded-[2.5rem] flex flex-col border transition-all duration-500 ${plan.highlight ? 'bg-[#BEF264] text-black border-[#BEF264]' : 'bg-white/5 text-white border-white/10'}`}>
          {plan.mostPopular && (
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
              <span className="bg-white text-black px-4 py-1.5 md:px-5 md:py-2 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] shadow-xl whitespace-nowrap"> Most Popular </span>
            </div>
          )}
          
          <h3 className="text-[13px] md:text-lg font-black uppercase tracking-[0.3em] mb-3 md:mb-6">{plan.name}</h3>
          
          <div className="flex flex-col mb-4 md:mb-10">
            {!plan.isUnlimited ? (
              <>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-[8px] md:text-[10px] font-bold uppercase tracking-widest ${plan.highlight ? 'text-black/40' : 'text-white/30'}`}>Was</span>
                  <span className={`relative text-xs md:text-sm font-bold ${plan.highlight ? 'text-black/40' : 'text-white/40'}`}> £{wasPrice} <span className="absolute inset-x-0 top-1/2 h-[1px] bg-red-500 -rotate-12" /> </span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-xs md:text-sm font-bold opacity-60">£</span>
                  <span className="text-3xl md:text-5xl font-black tracking-tighter">{plan.price}</span>
                  <span className="text-[9px] md:text-[10px] font-bold uppercase opacity-60 ml-1 tracking-widest">/mo</span>
                </div>
              </>
            ) : (
              <span className="text-2xl md:text-4xl font-black tracking-tighter italic uppercase">{plan.price}</span>
            )}
          </div>

          <ul className="space-y-1.5 md:space-y-4 mb-5 md:mb-12 md:flex-grow">
            {plan.features.map((f, idx) => (
              <li key={idx} className="flex items-start gap-2 md:gap-3">
                <SafeIcon icon={FiCheck} className={`w-3 h-3 mt-0.5 flex-shrink-0 ${plan.highlight ? 'text-black' : 'text-[#BEF264]'}`} />
                <span className={`text-[10px] md:text-[12px] font-medium uppercase tracking-[0.05em] leading-tight ${plan.highlight ? 'text-black/80' : 'text-white/70'}`}>{f}</span>
              </li>
            ))}
          </ul>

          <div className={`w-full p-3.5 md:p-5 rounded-xl md:rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] md:text-[11px] text-center transition-all transform group-hover:scale-[1.02] ${plan.highlight ? 'bg-black text-white' : 'bg-[#BEF264] text-black'}`}> Secure Slot </div>
        </div>

        {/* Back Face (Enquiry) */}
        <div className="absolute inset-0 backface-hidden p-5 md:p-10 rounded-[1.75rem] md:rounded-[2.5rem] bg-black border border-white/20 rotate-y-180 flex flex-col overflow-hidden">
          {status === 'success' ? (
            <div className="flex flex-col items-center justify-center h-full text-center animate-reveal" onClick={(e) => e.stopPropagation()}>
              <div className="w-12 h-12 rounded-full bg-[#BEF264]/10 flex items-center justify-center mb-6">
                <SafeIcon icon={FiCheck} className="w-6 h-6 text-[#BEF264]" />
              </div>
              <h3 className="text-white font-black uppercase tracking-widest text-lg mb-2">Request Received</h3>
              <p className="text-white/60 text-[10px] md:text-xs font-medium tracking-wide max-w-[200px]">
                We'll be in touch regarding the {plan.name} plan shortly.
              </p>
              <button 
                onClick={(e) => { e.stopPropagation(); setIsFlipped(false); setTimeout(() => setStatus('idle'), 500); }}
                className="mt-8 px-6 py-3 bg-white/5 hover:bg-[#BEF264] text-white hover:text-black rounded-xl text-[9px] font-black uppercase tracking-[0.2em] transition-all"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4 md:mb-8">
                <button onClick={handleBackButtonClick} className="flex items-center gap-2 text-[#BEF264] font-black uppercase tracking-[0.3em] text-[8px] md:text-[9px] hover:text-white transition-colors" >
                  <SafeIcon icon={FiArrowLeft} className="w-3 h-3" /> <span>Back</span>
                </button>
                <span className="text-white/20 font-black uppercase tracking-[0.3em] text-[8px] md:text-[9px]">Enquiry</span>
              </div>
              <form className="space-y-3 md:space-y-5 flex-grow" onClick={handleInputInteraction} onSubmit={handleSubmit}>
                <div className="space-y-0.5 md:space-y-1">
                  <label className="text-[7px] md:text-[8px] font-black uppercase tracking-[0.3em] text-[#BEF264]/60 ml-1">Full Name *</label>
                  <input 
                    type="text" 
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required 
                    placeholder="Identity" 
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 md:py-3 text-white text-[10px] md:text-[11px] outline-none focus:border-[#BEF264] transition-colors placeholder:text-white/10" 
                  />
                </div>
                <div className="space-y-0.5 md:space-y-1">
                  <label className="text-[7px] md:text-[8px] font-black uppercase tracking-[0.3em] text-[#BEF264]/60 ml-1">Email Address *</label>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required 
                    placeholder="Communication" 
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 md:py-3 text-white text-[10px] md:text-[11px] outline-none focus:border-[#BEF264] transition-colors placeholder:text-white/10" 
                  />
                </div>
                <div className="space-y-0.5 md:space-y-1">
                  <label className="text-[7px] md:text-[8px] font-black uppercase tracking-[0.3em] text-[#BEF264]/60 ml-1">Social Handle</label>
                  <input 
                    type="text" 
                    name="socialHandle"
                    value={formData.socialHandle}
                    onChange={handleInputChange}
                    placeholder="@username" 
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 md:py-3 text-white text-[10px] md:text-[11px] outline-none focus:border-[#BEF264] transition-colors placeholder:text-white/10" 
                  />
                </div>
                {status === 'error' && (
                   <div className="text-red-500 text-[9px] font-bold uppercase tracking-widest text-center">Something went wrong. Please try again.</div>
                )}
                <button 
                  type="submit" 
                  disabled={status === 'submitting'}
                  className="w-full mt-1 md:mt-4 bg-[#BEF264] text-black py-4 md:py-5 rounded-xl md:rounded-2xl font-black uppercase tracking-[0.3em] text-[10px] hover:bg-white transition-all transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                > 
                  {status === 'submitting' ? 'Processing...' : 'Continue'} 
                </button>
              </form>
              <div className="mt-2 md:mt-4 pt-2 md:pt-4 border-t border-white/5 flex justify-center">
                <span className="text-[7px] font-black uppercase tracking-[0.5em] text-white/10">Living Media Calibration</span>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default PricingCard;